import { useMemo } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { GapRepresentativeValue } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Badge,
  Box,
  Flex,
  LightModeSemanticColors,
  RadioButton,
  Select,
  type SelectOption,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import { FaGearRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { HelpIcon } from '~/components/common/HelpIcon'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { MultiSelect } from '~/components/common/MultiSelect'
import { FilterSection, FilterToolbar } from '~/components/common/filters'
import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { iconSize } from '~/constants/iconSize'
import { typography } from '~/constants/typography'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { convertAreaLabel } from '~/models/area'
import { civilDateToInputValue, parseDateInputValue } from '~/models/civilDate'
import type { UseFilterStateReturn } from '~/pages/SupplyGap/hooks/useFilterState'
import { REP_VALUE_OPTIONS } from '~/pages/SupplyGap/models/filterOptions'
import {
  type BreakdownMode,
  isBreakdownMode,
} from '~/pages/SupplyGap/models/supplyCategories'
import type { RangeType } from '~/pages/SupplyGap/models/types'
import { routePaths } from '~/routes/routePaths'

type Props = {
  filterState: UseFilterStateReturn
  businessAreaCodes: AreaCode[]
  /**
   * ワークフローケース由来で対象日 (日レンジの dayDate) が固定されているか
   * (issue #7131 / AC-WFW-006a Decision 5)。 true のとき日レンジの日付入力のみ
   * disabled にする。週 / 月の起点入力・rangeType 切替は対象外。
   */
  isDateLocked?: boolean
  /**
   * ケース対象日が確定済みか (= `lockedTargetDate !== undefined`)。 true の間
   * だけ Badge「ケース対象日に固定」を表示する (Design Note §States: 取得済の
   * みが対象)。省略時は false。
   */
  isTargetDateConfirmed?: boolean
}

const RANGE_OPTIONS: Array<{ value: RangeType; labelKey: string }> = [
  { value: 'day', labelKey: 'page:supplyGap.filter.rangeDay' },
  { value: 'week', labelKey: 'page:supplyGap.filter.rangeWeek' },
  { value: 'month', labelKey: 'page:supplyGap.filter.rangeMonth' },
]

// design `supply-gap.html` L190-195 の `#sg-breakdown` (radiogroup 2 択、 集約 が既定)。
const BREAKDOWN_OPTIONS: Array<{ value: BreakdownMode; labelKey: string }> = [
  { value: 'detail', labelKey: 'page:supplyGap.filter.breakdownDetail' },
  { value: 'aggregate', labelKey: 'page:supplyGap.filter.breakdownAggregate' },
]

// FilterSection の label htmlFor 対象にするための id (id 必須の CompactCivilDateField 用)。
const DAY_DATE_FIELD_ID = 'supply-gap-day-date'
const WEEK_START_FIELD_ID = 'supply-gap-week-start'
const MONTH_START_FIELD_ID = 'supply-gap-month-start'

// `FilterSection` の外に置く「〜間」 ラベルもフィルタ項目名なので同じ書体に揃える。
// 本画面の `FilterToolbar` は `labelVariant="formLabel"` (= textStyle `label` / md/600) なので、
// `filterLabel` (md/700) ではなく `label` を使う (行内の項目名と太さを揃える)。
const labelStyle: React.CSSProperties = {
  ...typography.label,
  whiteSpace: 'nowrap',
}

/**
 * design (L172-176 代表値 / L193-197 エリア) 準拠のラベル横 info tooltip。
 * `FilterSection` の `label` は string 専用のため、 アイコン付きラベルが要る
 * 2 箇所 (代表値 / エリア) のみ `FilterSection` を使わず本 helper で手組みする。
 */
const LabelWithInfo = ({
  label,
  tooltip,
}: {
  label: string
  tooltip: string
}): React.ReactElement => (
  <Text
    textStyle="label"
    alignItems="center"
    gap={1}
    display="inline-flex"
    color="Text.highEmphasis"
    whiteSpace="nowrap"
  >
    {label}
    <HelpIcon ariaLabel={label} content={tooltip} />
  </Text>
)

// chakra `Box` の `as` polymorphism は型上 `htmlFor` を認識しないため
// (SingularDayForm.tsx `BoxAsLabel` と同根の既知の型崩れ)、 label 用に絞った
// 型で cast する。
const BoxAsLabel = Box as unknown as React.FC<{
  as: 'label'
  htmlFor: string
  css?: Record<string, unknown>
  children?: React.ReactNode
}>

/**
 * `FilterSection` のラベルは `<Text>` であり `<label htmlFor>` ではないため、
 * 対応 input に accessible name が付かない (a11y 違反)。 視覚上は `FilterSection` の
 * ラベル文言で足りているので、 同じ文言の visually-hidden な `<label htmlFor>` を
 * 重ねて accessible name のみ補う (表示は変わらない)。
 */
const VisuallyHiddenLabel = ({
  htmlFor,
  children,
}: {
  htmlFor: string
  children: string
}): React.ReactElement => (
  <BoxAsLabel
    as="label"
    css={{
      position: 'absolute',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      clipPath: 'inset(50%)',
      whiteSpace: 'nowrap',
    }}
    htmlFor={htmlFor}
  >
    {children}
  </BoxAsLabel>
)

export const FilterBar = ({
  filterState,
  businessAreaCodes,
  isDateLocked = false,
  isTargetDateConfirmed = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { dateLockedReason } = useWorkflowLockReasons()
  const { filter } = filterState
  const isDay = filter.rangeType === 'day'

  // エリアの multi-select 操作 (design supply-gap.html L1197-1234 準拠のポジティブ
  // 選択モデル、 空 = 全業務エリア)。 最低 1 エリア表示の担保は
  // useGetSupplyDemandGap 側の resolveQueryAreaCodes による展開で行うため、
  // ここでは受け取った選択をそのまま渡すだけでよい (dead-click 修正: 旧実装の
  // 0 件 no-op guard は撤廃済み)。
  const handleAreaMultiSelectChange = (next: string[]): void => {
    filterState.setSelectedAreaCodes(next.map((v) => Number(v) as AreaCode))
  }

  const areaOptions = useMemo(
    () =>
      businessAreaCodes.map((code) => ({
        value: String(code),
        label: convertAreaLabel(code),
      })),
    // `convertAreaLabel` は内部で i18next.t を呼ぶため、 依存に `t` が要る。
    // `I18nProvider` は描画をブロックせず session RPC 取得後の useEffect で
    // `changeLanguage` するので、 **本 component の mount 後に言語が変わりうる**。
    // `[businessAreaCodes]` だけだと memo が再計算されず、 英語組織で日本語ラベルが
    // 残る (同ファイルの他 memo は `[t]` を持っており、 ここだけ不揃いだった)。
    [businessAreaCodes, t],
  )

  const rangeSelectOptions: SelectOption[] = useMemo(
    () =>
      RANGE_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
      })),
    [t],
  )
  const selectedRangeOption =
    rangeSelectOptions.find((opt) => opt.value === filter.rangeType) ?? null

  const repValueSelectOptions: SelectOption[] = useMemo(
    () =>
      REP_VALUE_OPTIONS.map((opt) => ({
        value: String(opt.value),
        label: t(opt.labelKey),
      })),
    [t],
  )
  const selectedRepValueOption =
    repValueSelectOptions.find(
      (opt) => opt.value === String(filter.representativeValue),
    ) ?? null

  const weekDurSelectOptions: SelectOption[] = useMemo(
    () =>
      filterState.weekDurOptions.map((n) => ({
        value: String(n),
        label: `${n}${t('page:supplyGap.filter.weeks')}`,
      })),
    [filterState.weekDurOptions, t],
  )
  const selectedWeekDurOption =
    weekDurSelectOptions.find((opt) => opt.value === String(filter.weekDur)) ??
    null

  const monthDurSelectOptions: SelectOption[] = useMemo(
    () =>
      filterState.monthDurOptions.map((n) => ({
        value: String(n),
        label: `${n}${t('page:supplyGap.filter.months')}`,
      })),
    [t],
  )
  const selectedMonthDurOption =
    monthDurSelectOptions.find(
      (opt) => opt.value === String(filter.monthDur),
    ) ?? null

  return (
    <FilterToolbar
      footer={
        !isDay ? (
          <div
            style={{
              fontSize: fontSizes.sm.value,
              color: LightModeSemanticColors.Text.midEmphasis.value,
              marginTop: '4px',
            }}
          >
            {t('page:supplyGap.filter.repValueNote', {
              value: (() => {
                const opt = REP_VALUE_OPTIONS.find(
                  (o) => o.value === filter.representativeValue,
                )
                return opt ? t(opt.labelKey) : ''
              })(),
            })}
          </div>
        ) : undefined
      }
      labelVariant="formLabel"
    >
      {/* 表示範囲。design は <label> だが、.filter-group が .ecl-toolbar の直接子でなく
          中間 wrapper div 越しのため .ecl-toolbar > .filter-group > label:first-child (700)
          が不適用となり、.filter-group > label:not(.ecl-checkbox):not(.ecl-radio) (600) が
          実際に効いている (計測で確認済)。行単位 labelVariant="formLabel" (=textStyle label/600) で
          design に exact 一致させる */}
      <FilterSection label={t('page:supplyGap.filter.rangeLabel')}>
        {/* 幅は外側の Box。 DS Select に chakraStyles を渡すと DS 側の chakraStyles が
            丸ごと置き換わり、 選択時背景・list の font-size・▽ 左の区切り線が失われる。 */}
        <Box minWidth="150px" css={selectFontFix}>
          <Select
            aria-label={t('page:supplyGap.filter.rangeLabel')}
            isMulti={false}
            // 一覧の sticky TH に menu が覆われるため body へ portal する。
            menuPortalTarget={document.body}
            onChange={(opt) => {
              if (opt !== null) {
                filterState.setRangeType(opt.value as RangeType)
              }
            }}
            options={rangeSelectOptions}
            selectedOptions={selectedRangeOption}
            size="md"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
          />
        </Box>
      </FilterSection>

      {/* 対象日 (label 書体の理由は 表示範囲 と同じ = 行単位 labelVariant)。
          DS 準拠 (CompactCivilDateField)。旧 raw <input type="date"> (32px / borderRadius
          4px / accessible name 無し) から置換 (2026-08-12)。 */}
      {filter.rangeType === 'day' && (
        <FilterSection label={t('page:supplyGap.filter.dayDate')}>
          <VisuallyHiddenLabel htmlFor={DAY_DATE_FIELD_ID}>
            {t('page:supplyGap.filter.dayDate')}
          </VisuallyHiddenLabel>
          <Box width={COMPACT_DATE_FIELD_WIDTH}>
            {(() => {
              // `DateStepper` の `bareDateInput` と同じ作法: 要素は 1 つだけ組み、
              // disabled 時だけ `ButtonTooltip` で条件 wrap する (2 ブランチの
              // 丸ごと複製を避ける)。
              const bareDayDateInput = (
                <CompactCivilDateField
                  disabled={isDateLocked}
                  id={DAY_DATE_FIELD_ID}
                  onChange={(d) => {
                    if (d !== undefined) {
                      filterState.setDayDate(civilDateToInputValue(d))
                    }
                  }}
                  value={parseDateInputValue(filter.dayDate)}
                />
              )
              return !isDateLocked ? (
                bareDayDateInput
              ) : (
                <ButtonTooltip content={dateLockedReason}>
                  {bareDayDateInput}
                </ButtonTooltip>
              )
            })()}
          </Box>
          {isTargetDateConfirmed && (
            <Badge status="info">{t('common:filter.caseLockedBadge')}</Badge>
          )}
        </FilterSection>
      )}

      {/* 週間 (label 書体の理由は 表示範囲 と同じ = 行単位 labelVariant)。
          DS 準拠 (CompactCivilDateField)。旧 raw <input type="date"> から置換 (2026-08-12)。
          注: 8080 (`_snapMonth` 相当の週頭 snap) は初期値のみ適用され、 任意日入力の
          snap は dev に未実装 (既存差分。今回のスコープ外、報告に記載)。 */}
      {filter.rangeType === 'week' && (
        <FilterSection label={t('page:supplyGap.filter.weekStart')}>
          <VisuallyHiddenLabel htmlFor={WEEK_START_FIELD_ID}>
            {t('page:supplyGap.filter.weekStart')}
          </VisuallyHiddenLabel>
          <Box width={COMPACT_DATE_FIELD_WIDTH}>
            <CompactCivilDateField
              id={WEEK_START_FIELD_ID}
              onChange={(d) => {
                if (d !== undefined) {
                  filterState.setWeekStart(civilDateToInputValue(d))
                }
              }}
              value={parseDateInputValue(filter.weekStart)}
            />
          </Box>
          <span style={labelStyle}>{t('page:supplyGap.filter.duration')}</span>
          <Box minWidth="120px" css={selectFontFix}>
            <Select
              aria-label={t('page:supplyGap.filter.duration')}
              isMulti={false}
              menuPortalTarget={document.body}
              onChange={(opt) => {
                if (opt !== null) {
                  filterState.setWeekDur(parseInt(opt.value, 10))
                }
              }}
              options={weekDurSelectOptions}
              selectedOptions={selectedWeekDurOption}
              size="md"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
            />
          </Box>
          {filterState.weekRangeLabel && (
            <span
              style={{
                ...typography.label,
                color: LightModeSemanticColors.Text.highEmphasis.value,
              }}
            >
              {filterState.weekRangeLabel}
            </span>
          )}
        </FilterSection>
      )}

      {/* 月間 (label 書体の理由は 表示範囲 と同じ = 行単位 labelVariant)。
          DS 準拠 (CompactCivilDateField)。旧 raw <input type="date"> から置換 (2026-08-12)。
          注: 8080 の任意日入力 → 月初 snap (`_snapMonth`) は dev 未実装 (既存差分。
          今回のスコープ外、報告に記載)。 */}
      {filter.rangeType === 'month' && (
        <FilterSection label={t('page:supplyGap.filter.monthStart')}>
          <VisuallyHiddenLabel htmlFor={MONTH_START_FIELD_ID}>
            {t('page:supplyGap.filter.monthStart')}
          </VisuallyHiddenLabel>
          <Box width={COMPACT_DATE_FIELD_WIDTH}>
            <CompactCivilDateField
              id={MONTH_START_FIELD_ID}
              onChange={(d) => {
                if (d !== undefined) {
                  filterState.setMonthStart(civilDateToInputValue(d))
                }
              }}
              value={parseDateInputValue(filter.monthStart)}
            />
          </Box>
          <span style={labelStyle}>{t('page:supplyGap.filter.duration')}</span>
          <Box minWidth="120px" css={selectFontFix}>
            <Select
              aria-label={t('page:supplyGap.filter.duration')}
              isMulti={false}
              menuPortalTarget={document.body}
              onChange={(opt) => {
                if (opt !== null) {
                  filterState.setMonthDur(parseInt(opt.value, 10))
                }
              }}
              options={monthDurSelectOptions}
              selectedOptions={selectedMonthDurOption}
              size="md"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
            />
          </Box>
          {filterState.monthRangeLabel && (
            <span
              style={{
                ...typography.label,
                color: LightModeSemanticColors.Text.highEmphasis.value,
              }}
            >
              {filterState.monthRangeLabel}
            </span>
          )}
        </FilterSection>
      )}

      {/* 代表値 (指定日以外)。design L172-176: ラベル横に info tooltip。
          選択は表示範囲と同じ DS Select (2026-10-15 デザイナー指示でラジオから変更。
          8080 ebalance-prototype#624)。各値の意味はラベル横の (?) に書く */}
      {!isDay && (
        <Flex alignItems="center" flexWrap="wrap" gap={2}>
          <LabelWithInfo
            label={t('page:supplyGap.filter.repValueLabel')}
            tooltip={t('page:supplyGap.filter.repValueTooltip')}
          />
          {/* 幅は最長の選択肢 (JA オフピーク) + DS の余白。表示範囲と同じ 150px だと
              オフピークを選んだ時だけ 160px に伸びて行がずれる */}
          <Box minWidth="160px" css={selectFontFix}>
            <Select
              aria-label={t('page:supplyGap.filter.repValueLabel')}
              isMulti={false}
              menuPortalTarget={document.body}
              onChange={(opt) => {
                if (opt !== null) {
                  filterState.setRepresentativeValue(
                    Number(opt.value) as GapRepresentativeValue,
                  )
                }
              }}
              options={repValueSelectOptions}
              selectedOptions={selectedRepValueOption}
              size="md"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
            />
          </Box>
        </Flex>
      )}

      {/* 供給内訳 (AC-SDG-004b / AC-SDG-004c)。design L189-195 の `.filter-group` =
          `.form-label` + radiogroup 2 択。 DS `RadioButton` に寄せる。 */}
      <FilterSection label={t('page:supplyGap.filter.breakdownLabel')}>
        <RadioButton
          direction="row"
          aria-label={t('page:supplyGap.filter.breakdownLabel')}
          name="supplyGap-breakdown"
          onValueChange={(details) => {
            // DS の RadioButton は value を string で返すので、 同 module の
            // type guard で絞り込んでから渡す (`as BreakdownMode` の cast だと
            // 選択肢を増減した時に型が嘘をつく)。
            if (isBreakdownMode(details.value)) {
              filterState.setBreakdownMode(details.value)
            }
          }}
          radios={BREAKDOWN_OPTIONS.map((opt) => ({
            label: t(opt.labelKey),
            value: opt.value,
          }))}
          value={filter.breakdownMode}
        />
      </FilterSection>

      {/* エリア。design L192-197: ラベル横に info tooltip。
          ラベルは全画面共通の「エリア」に統一 (旧「表示エリア」から改称)。
          2026-08-12: checkbox 群 (IndeterminateCheckboxGroup) → multi-select へ移行
          (dead-click 修正、design supply-gap.html L205-208 準拠)。 空 = 全業務エリア。 */}
      <Flex alignItems="center" flexWrap="wrap" gap={2}>
        <LabelWithInfo
          label={t('page:supplyGap.filter.area')}
          tooltip={t('page:supplyGap.filter.areaTooltip')}
        />
        <Box minWidth="180px">
          <MultiSelect
            aria-label={t('page:supplyGap.filter.area')}
            onChange={handleAreaMultiSelectChange}
            options={areaOptions}
            placeholder={t('page:supplyGap.filter.allAreas')}
            selected={filter.selectedAreaCodes.map(String)}
          />
        </Box>

        {/* 業務エリア設定変更の導線。生 <a href> はアプリ内 path でも
            フルリロードになるため RouterLink (asChild) で SPA 遷移させる。
            部品は DS `IconButton` (ghost)。design SoT が
            `design/supply/supply-gap.html:208` の `.ecl-icon-btn--ghost` で、
            `design/ui-feasibility/dictionary/components.md` の対応表も
            `.ecl-icon-btn → IconButton` (テキストリンクの `.ecl-text-link` ではない)。
            開き方は同じタブ (UI 辞書 link-target.md)。 行き先は個人設定の業務エリアの
            カード (`?focus=business-area`。 screens/supply-gap.md 1g)。
            アイコンだけのボタンなので名前は DS Tooltip でも出す (IconOnlyButton が持つ)。 */}
        <IconOnlyButton
          flexShrink={0}
          color="Text.midEmphasis"
          asChild
          label={t('page:supplyGap.filter.settingsLink')}
          size="sm"
          variant="ghost"
        >
          <RouterLink
            title={t('page:supplyGap.filter.settingsLinkTitle')}
            to={`${routePaths.settings.path}?focus=business-area`}
          >
            <FaGearRegular boxSize={iconSize.gridInline} aria-hidden="true" />
          </RouterLink>
        </IconOnlyButton>
      </Flex>
    </FilterToolbar>
  )
}
